// hooks/useExclusion.ts
import { useMemo } from 'react';
import type { InputNode } from '../types';
import { hasCompleteShopDateValue } from '../nodes/ShopDateSelectNode/utils';

const FILE_TYPES = new Set(['upload']);

export function computeConditionalHidden(
  config: InputNode[],
  values: Record<string, string | string[]>,
): Set<number> {
  const hiddenIndexes = new Set<number>();
  config.forEach((node, index) => {
    if (!node.conditional) return;
    const { dependsOn, condition, values: expectedValues } = node.conditional;
    const dependencyValue = values[dependsOn];
    const currentValue = Array.isArray(dependencyValue) ? dependencyValue[0] : dependencyValue;
    if (!currentValue) {
      hiddenIndexes.add(index);
      return;
    }

    let matches = true;
    switch (condition) {
      case 'in':
        matches = expectedValues.includes(currentValue);
        break;
      case 'notIn':
        matches = !expectedValues.includes(currentValue);
        break;
      case 'equals':
        matches = expectedValues.length > 0 && currentValue === expectedValues[0];
        break;
      case 'notEquals':
        matches = expectedValues.length > 0 && currentValue !== expectedValues[0];
        break;
    }
    if (!matches) hiddenIndexes.add(index);
  });
  return hiddenIndexes;
}

export type ExclusionResult = {
  /** 被互斥隐藏的节点 key（应跳过渲染） */
  excludedKeys: Set<string>;
  /** 被互斥隐藏的 separator 节点索引（按区域精确判定，区域间互相独立） */
  hiddenSeparatorIndexes: Set<number>;
  /** 被条件渲染隐藏的节点索引（separator 等无 key 节点也算）。
   *  渲染层依此跳过对应节点（不止靠 key）。 */
  conditionalHiddenIndexes: Set<number>;
};

/** 单节点是否「有值」：upload 看 files（含 batch `${key}-${i}`），其他（含 video-url/video-upload）看 values[key]。 */
function nodeHasValue(
  node: InputNode,
  values: Record<string, string | string[]>,
  files: Record<string, File | string | null>,
): boolean {
  if (!node.key) return false;
  if (node.type === 'shop-date-select') {
    return hasCompleteShopDateValue(values[node.key]);
  }
  if (FILE_TYPES.has(node.type)) {
    if (files[node.key] != null) return true;
    if (typeof node.maxCount === 'number' && node.maxCount > 1) {
      for (let i = 0; i < node.maxCount; i += 1) {
        if (files[`${node.key}-${i}`] != null) return true;
      }
    }
    return false;
  }
  const v = values[node.key];
  return Array.isArray(v)
    ? v.some((s) => s && s.trim().length > 0)
    : v !== undefined && v !== null && String(v).trim() !== '';
}

type RegionEntry = { node: InputNode; originalIndex: number };

/**
 * 把可见 config 切成「互斥区域」：连续带 exclusionGroup 的节点串成一个区域，
 * 不带 exclusionGroup 的节点（如普通文本）切断区域。
 * 区域之间完全独立——一对独立的「输入 或 上传」与另一对互不影响。
 *
 * 例：搜索主图分析（淘宝天猫，含「我的商品 / 我的竞品」两对独立选择）
 *   variable productId(myProduct) → separator → upload productImage(myProduct)
 *   ──text ";\n我的竞品是" 切断区域──
 *   multi-variable competitorIds(competitor) → separator → upload competitorImages(competitor)
 * 切成 2 个区域，每个区域内自己做「或」二选一，互不影响。
 */
function buildRegions(
  config: InputNode[],
  conditionalHiddenIndexes: Set<number>,
): RegionEntry[][] {
  const regions: RegionEntry[][] = [];
  let current: RegionEntry[] = [];
  config.forEach((node, idx) => {
    if (conditionalHiddenIndexes.has(idx)) return;
    if (node.exclusionGroup) {
      current.push({ node, originalIndex: idx });
    } else {
      if (current.length) regions.push(current);
      current = [];
    }
  });
  if (current.length) regions.push(current);
  return regions;
}

/**
 * 区域内有 separator 时：按 separator 切「选项段」，第一个有值的选项段保留，
 * 其余选项段全部 key 隐藏 + 区域内的 separator 隐藏。
 */
function applySeparatorExclusion(
  region: RegionEntry[],
  values: Record<string, string | string[]>,
  files: Record<string, File | string | null>,
  excludedKeys: Set<string>,
  hiddenSeparatorIndexes: Set<number>,
): void {
  const options: RegionEntry[][] = [];
  let cur: RegionEntry[] = [];
  const separatorEntries: RegionEntry[] = [];
  for (const entry of region) {
    if (entry.node.exclusionGroup === 'separator') {
      if (cur.length) options.push(cur);
      cur = [];
      separatorEntries.push(entry);
      continue;
    }
    if (entry.node.key) cur.push(entry);
  }
  if (cur.length) options.push(cur);

  const optionHasValue = options.map((opt) =>
    opt.some(({ node }) => nodeHasValue(node, values, files)),
  );
  const hasAnyValue = optionHasValue.some(Boolean);
  if (!hasAnyValue) return;
  const firstIdx = optionHasValue.findIndex(Boolean);

  options.forEach((opt, i) => {
    if (i === firstIdx) return;
    for (const { node } of opt) if (node.key) excludedKeys.add(node.key);
  });
  for (const sep of separatorEntries) hiddenSeparatorIndexes.add(sep.originalIndex);
}

/**
 * 区域内无 separator 时：跨组互斥（第一个有值的 group 保留，其余 group 隐藏）。
 * 单 group 的区域不会触发隐藏。
 */
function applyGroupExclusion(
  region: RegionEntry[],
  values: Record<string, string | string[]>,
  files: Record<string, File | string | null>,
  excludedKeys: Set<string>,
): void {
  const groupHasValue = new Map<string, boolean>();
  for (const { node } of region) {
    if (!node.exclusionGroup || !node.key) continue;
    if (nodeHasValue(node, values, files)) groupHasValue.set(node.exclusionGroup, true);
    else if (!groupHasValue.has(node.exclusionGroup)) groupHasValue.set(node.exclusionGroup, false);
  }
  let activeGroup: string | null = null;
  for (const { node } of region) {
    if (
      node.exclusionGroup
      && node.exclusionGroup !== 'separator'
      && groupHasValue.get(node.exclusionGroup)
    ) {
      activeGroup = node.exclusionGroup;
      break;
    }
  }
  if (!activeGroup) return;
  for (const { node } of region) {
    if (!node.key || !node.exclusionGroup) continue;
    if (node.exclusionGroup !== activeGroup) excludedKeys.add(node.key);
  }
}

/**
 * V1 parity 基础上增加区域化作用域：
 * - 按 config 顺序，连续带 exclusionGroup 的节点串成「互斥区域」；
 *   非 group 节点（如普通文本）切断区域。区域之间互不影响。
 * - 区域内有 separator → 按 separator 切「选项段」，第一段有值保留、其余段隐藏 + 该区域 separator 隐藏。
 * - 区域内无 separator → 跨组互斥，第一个有值的 group 保留、其余 group 隐藏。
 * 这样「我的商品（输入 或 上传）」与「我的竞品（输入 或 上传）」可作为两个独立区域并存，互不波及。
 */
export function computeExcluded(
  config: InputNode[],
  values: Record<string, string | string[]>,
  files: Record<string, File | string | null>,
): ExclusionResult {
  const conditionalHiddenIndexes = computeConditionalHidden(config, values);
  const conditionalHiddenKeys = new Set<string>();
  conditionalHiddenIndexes.forEach((idx) => {
    const k = config[idx]?.key;
    if (k) conditionalHiddenKeys.add(k);
  });

  const regions = buildRegions(config, conditionalHiddenIndexes);

  const excludedKeys = new Set<string>(conditionalHiddenKeys);
  const hiddenSeparatorIndexes = new Set<number>();

  for (const region of regions) {
    const hasSep = region.some(({ node }) => node.exclusionGroup === 'separator');
    if (hasSep) {
      applySeparatorExclusion(region, values, files, excludedKeys, hiddenSeparatorIndexes);
    } else {
      applyGroupExclusion(region, values, files, excludedKeys);
    }
  }

  return { excludedKeys, hiddenSeparatorIndexes, conditionalHiddenIndexes };
}

export function useExclusion(
  config: InputNode[],
  values: Record<string, string | string[]>,
  files: Record<string, File | string | null>,
): ExclusionResult {
  return useMemo(() => computeExcluded(config, values, files), [config, values, files]);
}
